import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiError, type Run, panels as panelsApi, runs as runsApi } from '../lib/api'
import { useMe } from '../lib/useMe'
import { AuthLayout, ErrorBox, PageLoading } from '../components/AuthLayout'
import { RunningStatus, ResultsStatus } from '../components/RunStatus'

const RUN_POLL_INTERVAL_MS = 3000

// Standalone view of a single run, reachable any time via its own URL — the piece that
// makes the wizard's own "je mag dit venster sluiten" (you can close this tab) promise
// actually true. Same polling logic and the same shared RunningStatus/ResultsStatus
// components as Onboarding.tsx uses right after starting a run (app-shell-and-navigation
// plan §1c) — this page and the wizard's last two steps render identically on purpose.
export function RunView() {
  const { id } = useParams<{ id: string }>()
  const runId = Number(id)
  const navigate = useNavigate()
  const me = useMe()

  const [run, setRun] = useState<Run | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (me.isError) navigate('/login')
  }, [me.isError, navigate])

  useEffect(() => {
    if (!runId || me.isLoading || me.isError) return
    let cancelled = false

    async function poll() {
      try {
        const r = await runsApi.get(runId)
        if (cancelled) return
        setRun(r)
        if (r.status === 'succeeded' || r.status === 'failed' || r.status === 'refunded') return
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true)
          return
        }
        // A transient poll failure just tries again on the next tick.
      }
      if (!cancelled) setTimeout(poll, RUN_POLL_INTERVAL_MS)
    }
    poll()
    return () => {
      cancelled = true
    }
  }, [runId, me.isLoading, me.isError])

  async function retryRun(panelId: number, languages?: string[]) {
    setError('')
    setBusy(true)
    try {
      const started = await panelsApi.run(panelId, languages)
      navigate(`/runs/${started.run_id}`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de meting niet opnieuw starten.')
    } finally {
      setBusy(false)
    }
  }

  if (me.isLoading) return <PageLoading />
  if (me.isError) return null

  if (notFound) {
    return (
      <AuthLayout title="Meting niet gevonden" wide>
        <ErrorBox>Deze meting bestaat niet (meer), of hoort niet bij jouw organisatie.</ErrorBox>
      </AuthLayout>
    )
  }

  const isRunning = !run || run.status === 'queued' || run.status === 'running'

  return (
    <AuthLayout title="Meting" wide>
      <ErrorBox>{error}</ErrorBox>
      {isRunning ? (
        <RunningStatus run={run} />
      ) : (
        <ResultsStatus
          run={run}
          doneLabel="Sluiten"
          onDone={() => navigate('/sites')}
          onRetryFailed={(languages) => run && retryRun(run.panel_id, languages)}
          busy={busy}
        />
      )}
    </AuthLayout>
  )
}
